import { Button, Col, Input, notification, Row } from "antd";
import React, { useState } from "react";
import { AiOutlineShareAlt } from "@react-icons/all-files/ai/AiOutlineShareAlt";
import { FiLinkedin } from "@react-icons/all-files/fi/FiLinkedin";
import { FiMail } from "@react-icons/all-files/fi/FiMail";
import { FiTwitter } from "@react-icons/all-files/fi/FiTwitter";
import { useDispatch } from "react-redux";
import styled, { CSSObject } from "styled-components";
import { setDialogTrainingState } from "../../../Actions/DigitalShareActions";
import { saveSharedReport } from "../../../Contracts/Axios/GPSInsightsAxios";
import { DialogTrainingState } from "../../../Enums/DialogTrainingState";
import { ReportTypeEnum } from "../../../Enums/ReportTypeEnum";
import { altGrayTextColor, deviceWidth } from "../../../globalStyles";
import { DEFAULT_NOTIFICATION_DURATION } from "../../../Logic/NotificationLogic";
import { Menu } from "../ReportStyles/Menu";
import { popupCenter, useWindowResize } from "../SharedActions";

interface ReportSharingMenuProps {
    jobId: string;
    title: string;
    reportType: ReportTypeEnum;
    disabled?: boolean;
    hideButtonText?: boolean;
    buttonStyle?: CSSObject;
    multiBrandOverview: boolean;
    onShareClick: () => void;
}

const FullHeightCol = styled(Col)`
  height: 100%;
`;

const ShareLinkWrapper = styled.a`
  color: #00000090;
  cursor: pointer;
  transition: color 0.3s, font-weight 0.3s;
  &:hover,
  &.active {
    background: #f5f6f7;
    color: ${altGrayTextColor};
  }
  &.active {
    font-weight: 500;
  }
`;

const iconSize = 24;

const ShareMenuImg = styled.img`
  height: ${iconSize}px;
`;

export const ReportSharingMenu = (props: ReportSharingMenuProps) => {
    const [open, setIsMenuOpen] = useState<boolean>();
    const [shareLink, setShareLink] = useState<string>("");
    const [generatingShareLink, setGeneratingShareLink]
        = useState<boolean>(false);

    const windowSize = useWindowResize();
    const dispatch = useDispatch();

    const openShareDialog = () => {
        props.onShareClick();
        setIsMenuOpen(false);
    };

    const onLoopNetClick = () => {
        openShareDialog();
        dispatch(setDialogTrainingState(DialogTrainingState.LoopNet));
    };

    const onCrexiClick = () => {
        openShareDialog();
        dispatch(setDialogTrainingState(DialogTrainingState.Crexi));
    };

    const onLinkButtonClick = () => {
        if (shareLink === "") {
            generateShareLink();
        } else {
            copyLink();
        }
    };

    const generateShareLink = () => {
        setGeneratingShareLink(true);
        saveSharedReport(props.jobId, (shareReportGuid) => {
            const reportUri = getReportUri(props.reportType, shareReportGuid);
            setShareLink(reportUri);
            setGeneratingShareLink(false);
        });
    };

    const copyLink = () => {
        navigator.clipboard.writeText(shareLink).then((value) => {
            notification.open({
                message: "Share link copied!",
                duration: DEFAULT_NOTIFICATION_DURATION,
            });
        });
    };

    return (
        <Menu
            title="Share"
            hideTitle={props.hideButtonText}
            icon={<AiOutlineShareAlt size={iconSize} />}
            type="filled"
            bgColor="black"
            color="white"
            isOpen={open}
            setIsMenuOpen={setIsMenuOpen}
            disabled={props.disabled}
            style={{ marginBottom: 0 }}
            buttonStyle={props.buttonStyle}
            wrapperStyle={{
                marginRight: "8px",
            }}
        >
            <div
                style={{
                    padding: "20px",
                }}
            >
                <Row style={{ marginBottom: 5 }}>
                    <Col span={24}>
                        <strong>Get a shareable link:</strong>
                    </Col>
                </Row>
                <Row
                    style={{
                        marginBottom: 14,
                        height: 40,
                    }}
                >
                    <FullHeightCol span={9}>
                        <Button
                            style={{
                                width: "100%",
                                height: "100%",
                                backgroundColor: "#2e3d49",
                                color: "white",
                                padding: 0,
                            }}
                            onClick={onLinkButtonClick}
                            disabled={generatingShareLink}
                        >
                            {shareLink === "" ? "Generate Link" : "Copy link"}
                        </Button>
                    </FullHeightCol>
                    {(shareLink !== "" || generatingShareLink) && (
                        <FullHeightCol span={15}>
                            <Input
                                style={{
                                    width: "100%",
                                    height: "100%",
                                }}
                                value={
                                    generatingShareLink ? "Generating, please wait" : shareLink
                                }
                                disabled={generatingShareLink}
                            />
                        </FullHeightCol>
                    )}
                </Row>
                <Row style={{ marginBottom: 5 }}>
                    <Col span={24}>
                        <strong>Share link via:</strong>
                    </Col>
                </Row>
                <Row gutter={16} style={{ marginBottom: 18 }}>
                    <Col span={4}>
                        <ShareLinkWrapper>
                            <FiMail
                                size={iconSize}
                                onClick={() => {
                                    setIsMenuOpen(false);
                                    saveSharedReport(props.jobId, (shareReportGuid) => {
                                        const reportUri
                                            = window.location.origin
                                            + `/Home/Share/${createShareableUrl(
                                                props.reportType,
                                                shareReportGuid
                                            )}`;

                                        const type: string
                                            = props.reportType === ReportTypeEnum.Mobilytics
                                                ? "Mobile Insight"
                                                : "Match Analysis";
                                        const subject = `Report for ${props.title}`;
                                        const body = `Check out this ${type} Report by Buxton.\n\n ${reportUri}`;

                                        window.location.href = `mailto:?subject=${encodeURIComponent(
                                            subject
                                        )}&body=${encodeURIComponent(body)}`;
                                    });
                                }}
                            />
                        </ShareLinkWrapper>
                    </Col>
                    <Col span={4}>
                        <ShareLinkWrapper>
                            <FiLinkedin
                                size={iconSize}
                                onClick={() => {
                                    setIsMenuOpen(false);
                                    const redirectWindow = popupCenter(
                                        "",
                                        "linkedInSharer",
                                        700,
                                        700
                                    );
                                    redirectWindow.document.write(
                                        "<html><head></head><body>Please wait while we load your report...</body></html>"
                                    );
                                    redirectWindow.document.close();
                                    saveSharedReport(props.jobId, (shareReportGuid) => {
                                        const reportUri: string = getReportUri(
                                            props.reportType,
                                            shareReportGuid
                                        );
                                        redirectWindow.location.href = `https://www.linkedin.com/shareArticle?mini=true&url=${reportUri}`;
                                        if (window.focus) redirectWindow.focus();
                                    });
                                }}
                            />
                        </ShareLinkWrapper>
                    </Col>
                    <Col span={4}>
                        <ShareLinkWrapper>
                            <FiTwitter
                                size={iconSize}
                                onClick={() => {
                                    setIsMenuOpen(false);
                                    const redirectWindow = popupCenter(
                                        "",
                                        "twitterSharer",
                                        700,
                                        700
                                    );
                                    redirectWindow.document.write(
                                        "<html><head></head><body>Please wait while we load your report...</body></html>"
                                    );
                                    redirectWindow.document.close();
                                    saveSharedReport(props.jobId, (shareReportGuid) => {
                                        const reportUri: string = getReportUri(
                                            props.reportType,
                                            shareReportGuid
                                        );
                                        redirectWindow.location.href = `https://twitter.com/intent/tweet?url=${reportUri}&via=buxtonco`;
                                        if (window.focus) redirectWindow.focus();
                                    });
                                }}
                            />
                        </ShareLinkWrapper>
                    </Col>
                </Row>
                {windowSize.width > deviceWidth.tabletXl && (
                    <>
                        <Row style={{ marginBottom: 5 }}>
                            <Col span={24}>
                                <strong>Export Sections to Share:</strong>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={4}>
                                <ShareLinkWrapper>
                                    <ShareMenuImg
                                        src="/BuxImages/loopnet_logo.png"
                                        onClick={onLoopNetClick}
                                    />
                                </ShareLinkWrapper>
                            </Col>
                            <Col span={4}>
                                <ShareLinkWrapper>
                                    <ShareMenuImg
                                        src="/BuxImages/crexi_logo.png"
                                        onClick={onCrexiClick}
                                    />
                                </ShareLinkWrapper>
                            </Col>
                        </Row>
                    </>
                )}
            </div>
        </Menu>
    );
};

const createShareableUrl = (
    reportType: ReportTypeEnum,
    shareReportGuid: string
): URLSearchParams => {
    return new URLSearchParams(`${reportType}_${shareReportGuid}`);
};

const getReportUri = (
    reportType: ReportTypeEnum,
    shareReportGuid: string
): string => {
    return `${window.location.origin}/Home/Share/${createShareableUrl(
        reportType,
        shareReportGuid
    )}`;
};
